"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.formQuestionsToFormWithTree = void 0;
const FormQuestionConfigRepetition_1 = require("../FormQuestionConfig/types/FormQuestionConfigRepetition");
const FormQuestionControllerRegistry_1 = __importDefault(require("../FormQuestionController/FormQuestionControllerRegistry"));
const FormQuestionType_1 = __importDefault(require("../FormQuestionType"));
const getEnabledChildQuestions_1 = __importDefault(require("../helpers/getEnabledChildQuestions"));
const getQuestionConfig_1 = __importDefault(require("../helpers/getQuestionConfig"));
const updateActiveQuestions_1 = __importDefault(require("../helpers/updateActiveQuestions"));
const addQuestion_1 = __importDefault(require("../mutations/addQuestion"));
const createEmptyFormAnswer_1 = __importDefault(require("../mutations/createEmptyFormAnswer"));
const createRootQuestionNode_1 = __importDefault(require("../mutations/createRootQuestionNode"));
/**
 * Split the form data into separate data structures.
 *
 *  * QuestionBank - a simple lookup of questionId -> Question
 *  * QuestionTree - a graph of questions and subquestions with an "enabled"
 * 	flag to denote the ones that are currently enabled.
 *  * AnswerBook - the current set of answers to the questions. The answers
 * 	are also stored in the question itself, for now, to support the original
 * 	implementation elsewhere in the system
 *  * QuestionLookupTable - a simple lookup of questionId -> QuestionNode in the
 * 	questionTree, in effect a flattened tree that enables reverse lookup
 *
 * @returns ```
 * {
 * 		questions,
 * 		questionTree,
 * 		answers,
 * 		questionLookup
 * }```
 */
const formQuestionsToFormWithTree = (formData) => {
    var _a;
    if (typeof formData === 'string') {
        formData = JSON.parse(formData);
    }
    const questionTree = (0, createRootQuestionNode_1.default)();
    const form = { questions: {}, answers: {}, rules: {} };
    const validFormData = (_a = formData === null || formData === void 0 ? void 0 : formData.filter(q => isAValidQT(q.type))) !== null && _a !== void 0 ? _a : [];
    for (const q of validFormData) {
        disassembleQuestion(form, questionTree, q);
    }
    const result = Object.assign(Object.assign({}, form), { questionTree });
    (0, updateActiveQuestions_1.default)(result);
    return result;
};
exports.formQuestionsToFormWithTree = formQuestionsToFormWithTree;
exports.default = exports.formQuestionsToFormWithTree;
function isAValidQT(qt) {
    return Object.values(FormQuestionType_1.default).indexOf(qt) >= 0;
}
const simplifyFormOption = (option) => {
    var _a;
    return option
        ? Object.assign(Object.assign({}, option), { subQuestions: ((_a = option.subQuestions) === null || _a === void 0 ? void 0 : _a.enabled)
                ? option.subQuestions.questions.map(q => q.qId)
                : [] }) : undefined;
};
const configToRule = (type, config) => {
    switch (type) {
        case FormQuestionType_1.default.CHECK: {
            const c = config;
            const { yes, no } = c, rest = __rest(c, ["yes", "no"]);
            return Object.assign(Object.assign({}, rest), { yes: simplifyFormOption(yes), no: simplifyFormOption(no) });
        }
        case FormQuestionType_1.default.LIST: {
            const c = config;
            const { options } = c, rest = __rest(c, ["options"]);
            const rule = Object.assign(Object.assign({}, rest), { options: options.map(o => simplifyFormOption(o)) });
            return rule;
        }
        case FormQuestionType_1.default.NUMBER: {
            const c = config;
            const { lessThan, moreThan } = c, rest = __rest(c, ["lessThan", "moreThan"]);
            const rule = Object.assign(Object.assign({}, rest), { lessThan: simplifyFormOption(lessThan), moreThan: simplifyFormOption(moreThan) });
            return rule;
        }
        case FormQuestionType_1.default.RATING: {
            const c = config;
            const { rating0, rating1, rating2, rating3, rating4 } = c, rest = __rest(c, ["rating0", "rating1", "rating2", "rating3", "rating4"]);
            return Object.assign(Object.assign({}, rest), { rating0: simplifyFormOption(rating0), rating1: simplifyFormOption(rating1), rating2: simplifyFormOption(rating2), rating3: simplifyFormOption(rating3), rating4: simplifyFormOption(rating4) });
        }
        case FormQuestionType_1.default.REPETITION:
        case FormQuestionType_1.default.SECTION:
        case FormQuestionType_1.default.QUESTIONSET: {
            const c = config;
            const { questions } = c, rest = __rest(c, ["questions"]);
            const rule = Object.assign(Object.assign({}, rest), { questions: questions.map(q => q.qId) });
            return rule;
        }
        default: {
            return config;
        }
    }
};
const createQuestion = (form, parent, q, id, answerIndex, prefix, repetitionAncestorId, totalRepetitions, expanded
// eslint-disable-next-line max-params
) => {
    var _a, _b;
    const subQuestions = (0, getEnabledChildQuestions_1.default)(q).filter(qq => isAValidQT(qq.type));
    let qNode = {
        questionId: q.qId,
        originalQuestionId: q.originalQId,
        nodeId: id,
        type: q.type,
        title: q.title,
        titleAlt: q.titleAlt,
    };
    if (repetitionAncestorId || q.type === FormQuestionType_1.default.REPETITION) {
        qNode = Object.assign(Object.assign({}, qNode), { repetitionAncestorId: repetitionAncestorId || id, repetitionIndex: answerIndex, totalRepetitions, expanded: expanded === undefined ? true : expanded });
    }
    const child = (0, addQuestion_1.default)(form, parent, qNode, ((_a = q.answer) === null || _a === void 0 ? void 0 : _a[answerIndex]) || (0, createEmptyFormAnswer_1.default)(), configToRule(q.type, FormQuestionControllerRegistry_1.default[q.type].createConfig((_b = q.config) === null || _b === void 0 ? void 0 : _b[q.type])));
    for (const subQ of subQuestions) {
        disassembleQuestion(form, child, subQ, answerIndex, q.type === FormQuestionType_1.default.REPETITION ? `${id}_` : prefix, q.type === FormQuestionType_1.default.REPETITION ? id : repetitionAncestorId, totalRepetitions);
    }
};
const disassembleQuestion = (form, parentNode, q, answerIndex = 0, prefix = '', repetitionAncestorId = null, totalRepetitions = 0
// eslint-disable-next-line max-params
) => {
    if (q.type === FormQuestionType_1.default.REPETITION) {
        const times = (0, FormQuestionConfigRepetition_1.getRepetitionTimes)((0, getQuestionConfig_1.default)(FormQuestionType_1.default.REPETITION, q));
        Array(times)
            .fill('')
            .forEach((a, j) => {
            createQuestion(form, parentNode, q, `${q.qId}_${j}`, j, '', repetitionAncestorId, times);
        });
    }
    else {
        createQuestion(form, parentNode, q, `${prefix}${q.qId}`, answerIndex, prefix, repetitionAncestorId, totalRepetitions);
    }
};
